import React, { useEffect, useMemo, useState } from "react";
import Resume from "../Resume";
import { notify } from "../Notification";
import { formatNoteDate } from "../../hooks/useClock";
import { FOLDERS, datedNotes, groupByDate, rowDate } from "../../data/notes";
import { ReactComponent as QuickNoteIcon } from "../../assets/svgs/sf-symbols/notes/quicknote.svg";
import { ReactComponent as SharedIcon } from "../../assets/svgs/sf-symbols/notes/person.crop.circle.svg";
import { ReactComponent as FolderIcon } from "../../assets/svgs/sf-symbols/notes/folder.svg";
import { ReactComponent as NewFolderIcon } from "../../assets/svgs/sf-symbols/notes/folder.badge.plus.svg";
import { ReactComponent as EllipsisIcon } from "../../assets/svgs/sf-symbols/notes/ellipsis.svg";
import { ReactComponent as ComposeIcon } from "../../assets/svgs/sf-symbols/notes/square.and.pencil.svg";
import { ReactComponent as ChecklistIcon } from "../../assets/svgs/sf-symbols/notes/checklist.svg";
import { ReactComponent as AttachIcon } from "../../assets/svgs/sf-symbols/notes/paperclip.svg";
import { ReactComponent as ShareIcon } from "../../assets/svgs/sf-symbols/notes/square.and.arrow.up.svg";
import { ReactComponent as LockIcon } from "../../assets/svgs/sf-symbols/notes/lock.fill.svg";
import { ReactComponent as ChevronRight } from "../../assets/svgs/sf-symbols/ios/chevron.right.svg";
import { ReactComponent as MarkupIcon } from "../../assets/svgs/sf-symbols/ios/pencil.tip.crop.circle.svg";
import { AppShell, BackButton, Decor, Screen, SearchField, useNavStack } from "./ios";

const FOLDER_ICONS = { quicknote: QuickNoteIcon, shared: SharedIcon, folder: FolderIcon };
const HOME_HINT = "ios-home-hint"; // sessionStorage key: the "how to leave" hint was shown

const FoldersScreen = ({ dated, open }) => {
  // Folders without a heading come first, then each account's (iCloud) under its name.
  const sections = FOLDERS.reduce((acc, f) => {
    const last = acc[acc.length - 1];
    if (last && last.name === (f.section || null)) last.folders.push(f);
    else acc.push({ name: f.section || null, folders: [f] });
    return acc;
  }, []);

  return (
    <Screen
      title="Folders"
      right={<Decor className="ios-pill">Edit</Decor>}
      bottom={
        <>
          <Decor>
            <NewFolderIcon className="ios-sym-newfolder" />
          </Decor>
          <Decor>
            <ComposeIcon className="ios-sym-compose" />
          </Decor>
        </>
      }
    >
      {sections.map((section) => (
        <section key={section.name || "top"}>
          {section.name && <h2 className="ios-section-title">{section.name}</h2>}
          <div className="ios-group">
            {section.folders.map((f) => {
              const Icon = FOLDER_ICONS[f.icon];
              return (
                <button type="button" key={f.id} className="ios-row ios-folder-row" onClick={() => open(f.id)}>
                  <Icon className="ios-folder-icon" aria-hidden="true" />
                  <span className="ios-folder-title">{f.title}</span>
                  <span className="ios-folder-count">{f.notes(dated).length}</span>
                  <ChevronRight className="ios-chevron" aria-hidden="true" />
                </button>
              );
            })}
          </div>
        </section>
      ))}
    </Screen>
  );
};

const ListScreen = ({ folder, dated, now, back, open }) => {
  const [query, setQuery] = useState("");
  const all = folder.notes(dated);
  const q = query.trim().toLowerCase();
  const shown = q ? all.filter((n) => `${n.title} ${n.preview}`.toLowerCase().includes(q)) : all;

  return (
    <Screen
      title={folder.title}
      subtitle={`${all.length} ${all.length === 1 ? "Note" : "Notes"}`}
      left={<BackButton onClick={back} />}
      right={
        <Decor>
          <EllipsisIcon className="ios-sym-ellipsis" />
        </Decor>
      }
      bottom={
        <>
          <SearchField
            className="ios-search-field glass glass--light"
            value={query}
            onChange={setQuery}
            label={`Search ${folder.title}`}
          />
          <Decor>
            <ComposeIcon className="ios-sym-compose" />
          </Decor>
        </>
      }
    >
      {groupByDate(shown, now).map((g) => (
        <section key={g.name}>
          <h2 className="ios-section-title">{g.name}</h2>
          <div className="ios-group">
            {g.notes.map((n) => (
              <button type="button" key={n.id} className="ios-row ios-note-row" onClick={() => open(n.id)}>
                <span className="ios-note-title">{n.title}</span>
                <span className="ios-note-meta">
                  <span className="ios-note-date">{rowDate(n.date, now)}</span>
                  <span className="ios-note-preview">{n.preview}</span>
                </span>
              </button>
            ))}
          </div>
        </section>
      ))}
      {!shown.length && <p className="ios-empty">{q ? "No Results" : "No Notes"}</p>}
    </Screen>
  );
};

const NoteScreen = ({ note, back, openResume }) => (
  <Screen
    scrollTestId="notes-scroll"
    left={<BackButton onClick={back} />}
    right={
      <Decor className="ios-capsule">
        <span>
          <ShareIcon className="ios-sym-share" />
        </span>
        <span>
          <EllipsisIcon className="ios-sym-ellipsis" />
        </span>
      </Decor>
    }
    bottom={
      <>
        <Decor className="ios-capsule">
          <span>
            <ChecklistIcon className="ios-sym-checklist" />
          </span>
          <span>
            <AttachIcon className="ios-sym-attach" />
          </span>
          <span>
            <MarkupIcon className="ios-sym-markup" />
          </span>
        </Decor>
        <Decor>
          <ComposeIcon className="ios-sym-compose" />
        </Decor>
      </>
    }
  >
    {note.id === "resume" ? (
      <div className="editor-main ios-note">
        <p className="note-date">{formatNoteDate(note.date)}</p>
        <Resume compact />
      </div>
    ) : (
      <div className="locked-note ios-locked">
        <LockIcon className="ios-lock" aria-hidden="true" />
        <p className="mt-4 text-[20px] font-semibold">This note is locked.</p>
        <p className="mt-1 text-[15px] text-[rgba(235,235,245,0.6)]">Only my resume is unlocked.</p>
        <button
          type="button"
          onClick={openResume}
          className="mt-6 h-11 px-6 rounded-full bg-[#ffd60a] text-[16px] font-semibold text-black"
        >
          Open my resume
        </button>
      </div>
    )}
  </Screen>
);

// Notes on iPhone (iOS 26): Folders, then a folder's notes grouped by date, then the note, each
// screen sliding in over the last. The Home Screen widget opens straight onto the resume with the
// folder and the folder list behind it, as tapping a note widget does on iOS. The bar at the
// bottom goes back to the Home Screen. Opened from Search with an `anchor` (an element id in the
// resume), it scrolls there once the app has zoomed open and briefly highlights it.
const NotesApp = ({ now, onClose, anchor }) => {
  const dated = useMemo(
    () => datedNotes(now),
    // Re-date only when the day changes, not every clock tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [now.toDateString()]
  );
  const nav = useNavStack([
    { screen: "folders" },
    { screen: "list", folderId: "notes" },
    { screen: "note", noteId: "resume" },
  ]);

  useEffect(() => {
    if (!anchor) return undefined;
    const id = setTimeout(() => {
      const el = document.getElementById(anchor);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      el.classList.remove("resume-flash");
      void el.offsetWidth; // restart the highlight if it's already playing
      el.classList.add("resume-flash");
    }, 450);
    return () => clearTimeout(id);
  }, [anchor]);

  const back = () => {
    // Back to the folder list: the only way out from there is the bar at the bottom, so say so once.
    if (nav.stack.length === 2) {
      try {
        if (!sessionStorage.getItem(HOME_HINT)) {
          sessionStorage.setItem(HOME_HINT, "1");
          notify("To leave Notes, tap or swipe up on the bar at the bottom of the screen.");
        }
      } catch (e) {}
    }
    nav.back();
  };

  // The locked note's "Open my resume" swaps it for the resume in place.
  const openResume = () => nav.replaceTop({ screen: "note", noteId: "resume" });

  const render = (entry) => {
    if (entry.screen === "folders") {
      return <FoldersScreen dated={dated} open={(folderId) => nav.push({ screen: "list", folderId })} />;
    }
    if (entry.screen === "list") {
      return (
        <ListScreen
          folder={FOLDERS.find((f) => f.id === entry.folderId)}
          dated={dated}
          now={now}
          back={back}
          open={(noteId) => nav.push({ screen: "note", noteId })}
        />
      );
    }
    return <NoteScreen note={dated.find((n) => n.id === entry.noteId)} back={back} openResume={openResume} />;
  };

  return (
    <AppShell label="Notes" origin="61% 92%" className="ios-app--notes" onClose={onClose}>
      {nav.pages.map((entry) => (
        <div key={entry.key} {...nav.pageProps(entry, entry.screen === "note" ? "" : "ios-page--grouped")}>
          {render(entry)}
        </div>
      ))}
    </AppShell>
  );
};

export default NotesApp;
